import { useEffect, useMemo, useState } from "react";

import {
  FLOW_EVENTS,
  activeFilterCount,
  addressOf,
  ageDistribution,
  ageOf,
  applyFilter,
  choose,
  countByWithPriority,
  dateOnly,
  daysOverdue,
  exportFilename,
  facetBase,
  facets,
  flowBase,
  flowSeries,
  formatDayMonthYear,
  formatDayRange,
  formatMonthShort,
  formatNumber,
  headlineFigures,
  overdueSummary,
  periodRange,
  toCsv,
  untouchedFilter,
  weekNumberOf,
  workBase,
  workCellFilter,
  workTable,
  type CountedGroup,
  type EmptyLabels,
  type FlowEvent,
  type FlowPeriod,
  type FlowUnit,
  type Locale,
  type PrioritySplit,
  type QualityAuditsPayload,
  type QualityFilter,
  type ReportRow,
  type TrendVerdict,
  type WorkLine,
  type WorkTable,
  type WorkTrend,
} from "@quality-field/shared";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCheck,
  ChevronRight,
  Clock,
  Download,
  Maximize2,
  Minus,
  Send,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";

import { Dialog } from "@/components/Dialog";
import { SortableHead, useTableView, type SortColumn, type TableView } from "@/components/SortableTable";
import { CHART_PALETTE, GroupedBars } from "@/components/charts/GroupedBars";
import { Donut } from "@/components/charts/Donut";
import { HBars } from "@/components/charts/HBars";
import { useI18n } from "@/i18n/useI18n";
import { apiFetch } from "@/lib/api-client";

/**
 * The Quality Audits report.
 *
 * ⚠ Every figure is computed from the row array the API served — never fetched as a
 * conclusion. Tiles, charts and (later) the export are the same function of the same rows,
 * so they cannot drift apart.
 *
 * ⚠ **Clicking a bar sets a filter.** The drill-down and the filter controls are ONE piece
 * of state, deliberately: two would eventually disagree about what is on screen, and the
 * breadcrumb is simply that state read out loud.
 *
 * ⚠ Scoping already happened, server-side. Nothing here may widen a row set — `applyFilter`
 * can only ever remove rows, which is tested in shared.
 */

const PRIORITY_COLOUR: Record<string, string> = {
  P1: CHART_PALETTE.red!,
  P2: CHART_PALETTE.amber!,
  P3: CHART_PALETTE.blue!,
};

/**
 * The Type VLS/FO bars: Unifiber's purple (`--uf-purple`), a hue that no priority, age or
 * deadline uses on this page — so a type bar cannot be misread as one of those.
 */
const TYPE_COLOUR = "var(--uf-purple)";

/**
 * The in-and-out chart's three series, in their fixed order.
 *
 * ⚠ Run through the dataviz palette validator on 28 Sep 2026 (light mode — the app has no
 * dark one): all six checks pass, worst adjacent pair ΔE 10.1 under protanopia, ≥ 3:1 on
 * white. None is a priority's red, amber or blue. Purple is also the Type chart's single
 * series, which carries no identity across panels; the legend names these three.
 */
const FLOW_COLOUR: Record<FlowEvent, string> = {
  created: "#84257a",
  done: "#ea580c",
  validated: "#059669",
};

/** Where a click that picks tickets takes the reader: the rows table, with its export. */
const ROWS_ANCHOR = "report-rows";

/** `S39` / `W39`, or `sept. 26` — a period as a bar's axis label. */
function periodLabel(key: string, unit: FlowUnit, t: (key: string, params?: Record<string, string | number>) => string, locale: Locale): string {
  return unit === "week" ? t("report.flow.week", { n: weekNumberOf(key) }) : `${formatMonthShort(key, locale)} ${key.slice(2, 4)}`;
}

/** `S39 (21–27 sept. 2026)` or `sept. 2026` — a period in a sentence, where it has room. */
function periodText(period: Pick<FlowPeriod, "key" | "unit">, t: (key: string, params?: Record<string, string | number>) => string, locale: Locale): string {
  const { first, last } = periodRange(period.key, period.unit);
  return period.unit === "week"
    ? `${periodLabel(period.key, "week", t, locale)} (${formatDayRange(first, last, locale)})`
    : `${formatMonthShort(period.key, locale)} ${period.key.slice(0, 4)}`;
}

/**
 * A bar's composition, in a fixed order.
 *
 * ⚠ P1, P2, P3, then the rest — never sorted by size. A bar whose pieces reorder between
 * rows cannot be compared to the bar above it, which is the whole reason for drawing them
 * side by side. The same order as the donut, the tile pills and the deepest drill level.
 *
 * `other` is every punch nobody prioritised. It is drawn in grey and it is what makes the
 * pieces fill the bar exactly.
 */
function segmentsOf(split: PrioritySplit, t: (key: string) => string) {
  return [
    { label: "P1", n: split.P1, color: PRIORITY_COLOUR.P1! },
    { label: "P2", n: split.P2, color: PRIORITY_COLOUR.P2! },
    { label: "P3", n: split.P3, color: PRIORITY_COLOUR.P3! },
    { label: t("report.legend.other"), n: split.other, color: CHART_PALETTE.slate! },
  ];
}

/** The key to the colours. Without it a striped bar is decoration rather than information. */
function PriorityLegend() {
  const { t } = useI18n();
  const items = [
    { label: "P1", color: PRIORITY_COLOUR.P1! },
    { label: "P2", color: PRIORITY_COLOUR.P2! },
    { label: "P3", color: PRIORITY_COLOUR.P3! },
    { label: t("report.legend.other"), color: CHART_PALETTE.slate! },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function Tile({
  eyebrow,
  value,
  icon,
  children,
  note,
}: {
  eyebrow: string;
  value: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="eyebrow">{eyebrow}</p>
        <span className="text-slate-300">{icon}</span>
      </div>
      <p className="mt-2 text-3xl font-bold tabular-nums text-slate-800">{value}</p>
      {children && <div className="mt-3 flex flex-wrap gap-1.5">{children}</div>}
      {note && <p className="mt-3 text-xs leading-relaxed text-slate-400">{note}</p>}
    </div>
  );
}

/**
 * The fourth tile: the door to the contractors' work (feature 29, 29 Sep 2026).
 *
 * ⚠ No figure and no link on it (Jérémy): a title, and a picture that only says "a click opens
 * more". The picture is a FIXED pictogram of the dialog's table, never data — a sparkline here
 * would be read as a figure, which is exactly what this tile is not. The whole tile is the
 * button.
 *
 * It replaced "last week's balance" (in minus validated) the day after that shipped: a balance
 * moves with the auditors' intake and with Réginald's validations, not with the contractors'
 * work — and a negative one read as bad news when it was good news.
 */
function WorkTile({ onOpen }: { onOpen: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-label={t("report.work.tile.open")}
      className="group flex w-full flex-col rounded-xl border border-slate-200 bg-white p-5 text-left transition hover:border-rose-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-200"
    >
      <div className="flex w-full items-start justify-between gap-3">
        <p className="eyebrow">{t("report.work.tile.eyebrow")}</p>
        <span className="text-slate-300 transition group-hover:text-brand-rose">
          <TrendingUp className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 text-xl font-bold leading-tight text-slate-800">{t("report.work.tile.title")}</p>
      <div
        className="relative mt-4 flex w-full flex-1 items-center overflow-hidden rounded-lg px-2 py-4"
        style={{ background: "linear-gradient(135deg, rgba(132,37,122,.06), rgba(237,18,95,.06) 55%, rgba(244,123,59,.10))" }}
      >
        <WorkPictogram />
        <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-brand-rose shadow-sm transition group-hover:scale-110">
          <Maximize2 className="h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
}

/** ⚠ Decorative, never data: grey bars for names, tints of the "done" orange, a fixed pattern. */
const PICTOGRAM_TINTS = ["#ffedd5", "#fed7aa", "#fdba74", "#fb923c", "#ea580c"];
const PICTOGRAM: number[][] = [
  [4, 2, 0, -1, 1, 3, 4],
  [-1, -1, 1, -1, -1, 0, 2],
  [2, 3, 2, -1, -1, -1, 0],
  [-1, 0, -1, -1, 1, -1, -1],
];

function WorkPictogram() {
  return (
    <svg viewBox="0 0 200 76" className="w-full" style={{ height: 76 }} aria-hidden="true">
      {PICTOGRAM.map((line, i) => (
        <g key={i}>
          <rect x={10} y={9 + i * 17} width={30} height={7} rx={3.5} fill="#cbd5e1" />
          {line.map((k, j) => (
            <rect key={j} x={50 + j * 17} y={6 + i * 17} width={13} height={13} rx={3} fill={k < 0 ? "#f1f5f9" : PICTOGRAM_TINTS[k]} />
          ))}
        </g>
      ))}
    </svg>
  );
}

/** Rising, falling, the same, nothing — a status, so it wears the status pills and an icon. */
const TREND_LOOK: Record<TrendVerdict, { pill: string; Icon: LucideIcon }> = {
  up: { pill: "pill-emerald", Icon: TrendingUp },
  down: { pill: "pill-red", Icon: TrendingDown },
  flat: { pill: "pill-slate", Icon: ArrowRight },
  none: { pill: "pill-slate", Icon: Minus },
};

function TrendBadge({ trend, weekRange }: { trend: WorkTrend; weekRange: (keys: string[]) => string }) {
  const { t } = useI18n();
  const { pill, Icon } = TREND_LOOK[trend.verdict];
  return (
    <span
      className={`pill ${pill}`}
      title={t("report.work.trend.detail", {
        recent: formatNumber(trend.recent),
        recentRange: weekRange(trend.recentWeeks),
        before: formatNumber(trend.before),
        beforeRange: weekRange(trend.beforeWeeks),
      })}
    >
      <Icon className="h-3 w-3" />
      {t(`report.work.trend.${trend.verdict}`)}
    </span>
  );
}

/**
 * A month's count as a tint of the "done" orange: one hue, light to dark. Square-root, so the
 * ones and twos stay visible beside Jacops' 62 of June 2026.
 */
function heatStyle(n: number, max: number): React.CSSProperties {
  const k = Math.sqrt(Math.min(n, max) / Math.max(max, 1));
  const mix = (from: number, to: number) => Math.round(from + (to - from) * k);
  return { background: `rgb(${mix(255, 194)}, ${mix(237, 65)}, ${mix(213, 12)})`, color: k > 0.55 ? "#fff" : "#7c2d12" };
}

/**
 * The contractors side by side, month by month — the dialog's table.
 *
 * ⚠ Every number that is a button counts exactly what clicking it shows (`workCellFilter`,
 * `untouchedFilter`, tested as a property in shared), and every click sets the page's one
 * filter: the dialog keeps no state of its own.
 */
function WorkTableView({
  work,
  filter,
  noPartner,
  onPartner,
  onCell,
  onUntouched,
}: {
  work: WorkTable;
  filter: QualityFilter;
  noPartner: string;
  /** `undefined` is the total line: back to every contractor. */
  onPartner: (partner: string | null | undefined) => void;
  onCell: (partner: string | null, monthKey: string) => void;
  onUntouched: (partner: string | null) => void;
}) {
  const { t, locale } = useI18n();
  const week = (key: string) => t("report.flow.week", { n: weekNumberOf(key) });
  const weekRange = (keys: string[]) => `${week(keys[0]!)}–${week(keys[keys.length - 1]!)}`;
  const monthName = (key: string) => `${formatMonthShort(key, locale)} ${key.slice(0, 4)}`;
  const max = Math.max(1, ...work.contractors.flatMap((c) => c.done));

  const line = (partner: string | null | undefined, w: WorkLine) => {
    const isTotal = partner === undefined;
    const label = isTotal ? t("report.work.total") : (partner ?? noPartner);
    const selected = isTotal ? filter.partner === undefined : filter.partner === partner;
    const untouchedChosen = !isTotal && filter.partner === partner && filter.statusContractor === null;
    return (
      <tr key={isTotal ? "\u0000total" : (partner ?? "\u0000none")} className={selected ? "bg-rose-50/60" : undefined}>
        <td className={`${isTotal ? "border-t border-slate-200" : ""} ${selected ? "shadow-[inset_3px_0_0_#ed125f]" : ""}`}>
          <button
            type="button"
            onClick={() => onPartner(partner)}
            title={isTotal ? t("report.work.name.clear") : t("report.work.name.hint", { partner: label })}
            className={`text-left font-semibold hover:text-brand-rose hover:underline ${isTotal ? "text-slate-800" : "text-slate-700"}`}
          >
            {label}
          </button>
        </td>
        {work.months.map((m, i) => {
          const n = w.done[i]!;
          const border = isTotal ? "border-t border-slate-200" : undefined;
          if (n === 0) {
            return (
              <td key={m.key} className={border}>
                <span className="text-slate-300">·</span>
              </td>
            );
          }
          if (isTotal) {
            return (
              <td key={m.key} className={border}>
                <span className="heat-cell" style={heatStyle(n, max)}>
                  {formatNumber(n)}
                </span>
              </td>
            );
          }
          const chosen =
            filter.partner === partner && filter.period?.event === "done" && filter.period.unit === "month" && filter.period.key === m.key;
          return (
            <td key={m.key}>
              <button
                type="button"
                className={`heat-cell${chosen ? " is-selected" : ""}`}
                style={heatStyle(n, max)}
                aria-pressed={chosen}
                onClick={() => onCell(partner, m.key)}
                title={t("report.work.cell.hint", { partner: label, month: monthName(m.key), n: formatNumber(n) })}
              >
                {formatNumber(n)}
              </button>
            </td>
          );
        })}
        <td className={`sep ${isTotal ? "border-t border-slate-200" : ""}`}>
          <TrendBadge trend={w.trend} weekRange={weekRange} />
        </td>
        <td className={isTotal ? "border-t border-slate-200 font-bold text-slate-800" : "text-slate-600"}>{formatNumber(w.open)}</td>
        <td className={isTotal ? "border-t border-slate-200 font-bold text-slate-800" : "text-slate-600"}>
          {isTotal || w.untouched === 0 ? (
            formatNumber(w.untouched)
          ) : (
            <button
              type="button"
              aria-pressed={untouchedChosen}
              onClick={() => onUntouched(partner)}
              title={t("report.work.untouched.hint", { partner: label, n: formatNumber(w.untouched) })}
              className={`hover:text-brand-rose hover:underline ${untouchedChosen ? "font-bold text-brand-rose" : ""}`}
            >
              {formatNumber(w.untouched)}
            </button>
          )}
        </td>
        <td className={isTotal ? "border-t border-slate-200 font-bold text-slate-800" : "text-slate-600"}>
          {w.sentBack > 0 ? formatNumber(w.sentBack) : <span className="text-slate-300">·</span>}
        </td>
      </tr>
    );
  };

  return (
    <table className="tbl tbl-compact">
      <thead>
        <tr>
          <th>{t("report.work.col.contractor")}</th>
          {work.months.map((m) => (
            <th key={m.key}>
              {formatMonthShort(m.key, locale)} {m.key.slice(2, 4)}
              {m.partial ? "*" : ""}
            </th>
          ))}
          <th className="sep">{t("report.work.col.trend")}</th>
          <th>{t("report.work.col.open")}</th>
          <th>{t("report.work.col.untouched")}</th>
          <th>{t("report.work.col.sentBack")}</th>
        </tr>
      </thead>
      <tbody>
        {work.contractors.map((c) => line(c.partner, c))}
        {line(undefined, work.total)}
      </tbody>
    </table>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4">
        <p className="eyebrow">{title}</p>
        {subtitle && <p className="mt-1 text-xs text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/** A dropdown built from the snapshot's own values, with the count on every option. */
function FacetSelect({
  label,
  groups,
  value,
  onChange,
}: {
  label: string;
  groups: CountedGroup[];
  value: string | null | undefined;
  onChange: (value: string | null | undefined) => void;
}) {
  // `undefined` (no filter), `null` (rows with no value) and a label are three different
  // things, so they get three different option values rather than two.
  const { t } = useI18n();
  const current = value === undefined ? "" : value === null ? "\u0000empty" : value;
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="text-slate-400">{label}</span>
      <select
        className="field h-8 w-auto min-w-[9rem] text-xs"
        value={current}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? undefined : v === "\u0000empty" ? null : v);
        }}
      >
        <option value="">{t("report.filter.all")}</option>
        {groups.map((g) => (
          <option key={g.label} value={g.value === null ? "\u0000empty" : g.value}>
            {g.label} ({g.n})
          </option>
        ))}
      </select>
    </label>
  );
}

export function QualityAudits() {
  const [data, setData] = useState<QualityAuditsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QualityFilter>({});
  const [flowUnit, setFlowUnit] = useState<FlowUnit>("week");
  const [workOpen, setWorkOpen] = useState(false);
  const { t, locale } = useI18n();

  useEffect(() => {
    let cancelled = false;
    apiFetch<QualityAuditsPayload>("/reports/quality-audits")
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the report");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = data?.rows ?? [];

  // Everything on screen derives from the rows and one filter. A filter is a shorter array.
  const shown = useMemo(() => applyFilter(rows, filter), [rows, filter]);
  const f = useMemo(() => headlineFigures(shown), [shown]);
  // ⚠ The heading for rows whose column is empty is OURS, not the board's, so it is
  // translated. Everything else on the lists and bars is a board label and is not.
  const empty = useMemo<EmptyLabels>(
    () => ({
      partner: t("group.noPartner"),
      area: t("group.noCommune"),
      priority: t("group.noPriority"),
      statusUnifiber: t("group.noStatus"),
      statusContractor: t("group.noAnswer"),
      typeVlsFo: t("group.noType"),
    }),
    [t],
  );
  // ⚠ Every option of every list and chart counts exactly what choosing it would show — so
  // each one is counted without its own filter (`facetBase`), and the value already chosen
  // is highlighted instead of being left alone on a collapsed list.
  const available = useMemo(() => facets(rows, filter, empty), [rows, filter, empty]);
  const byType = available.typeVlsFo;

  // The table's sort lives here rather than in the table, because the export follows it:
  // the file holds the rows in the order the screen shows them.
  const runDate = data?.snapshot?.runDate ?? null;
  const sortAsOf = useMemo(() => (runDate === null ? null : dateOnly(runDate)), [runDate]);
  const columns = useMemo(() => rowColumns(t, sortAsOf), [t, sortAsOf]);
  const table = useTableView(shown, columns);

  // ── In and out (feature 27). The chart counts every filter but "open only" and the period
  // itself (`flowBase`). While a period is chosen the chart shows ITS unit, so the chosen bar
  // is always on screen, and can be clicked off again. Since 29 Sep 2026 it lives in the
  // contractors' dialog, under their table: the page is lighter by a chart, and nothing on
  // screen is shown twice.
  const shownUnit: FlowUnit = filter.period?.unit ?? flowUnit;
  const inOutRows = useMemo(() => flowBase(rows, filter), [rows, filter]);
  const inOut = useMemo(
    () => (runDate === null ? null : flowSeries(inOutRows, shownUnit, runDate)),
    [inOutRows, shownUnit, runDate],
  );
  // ── The contractors' work (feature 29), counted without the filters its clicks set.
  const work = useMemo(() => (runDate === null ? null : workTable(workBase(rows, filter), runDate)), [rows, filter, runDate]);

  if (error) {
    return (
      <div className="px-4 py-8 lg:px-8">
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-5 py-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div>
            <p className="font-semibold text-red-800">{t("report.error.title")}</p>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <p className="px-4 py-8 text-sm text-slate-400 lg:px-8">{t("report.loading")}</p>;

  // ⚠ No snapshot is an honest empty screen, never a screen of zeros — zeros would read as
  // "the backlog is clear", which is the opposite of "we have not measured".
  if (!data.snapshot) {
    return (
      <div className="px-4 py-16 lg:px-8">
        <div className="mx-auto max-w-md rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
            <CalendarClock className="h-5 w-5" />
          </div>
          <h3 className="font-semibold text-slate-700">{t("report.noSnapshot.title")}</h3>
          <p className="mt-2 text-sm text-slate-500">{t("report.noSnapshot.body")}</p>
        </div>
      </div>
    );
  }

  const { snapshot, scopeLabels } = data;
  // ⚠ The SNAPSHOT's day, never today: an age recomputed at read time would tick over at
  // midnight while the figures it describes are a morning fact.
  const asOf = dateOnly(snapshot.runDate);
  const ages = ageDistribution(shown, asOf);
  const overdue = overdueSummary(shown, asOf);
  // Every change goes through `choose`, which clears what the change makes meaningless.
  const set = (patch: QualityFilter) => setFilter((prev) => choose(prev, patch));

  // ── The contractors' dialog. A click that picks a CONTRACTOR keeps it open — the chart
  // under the table follows; a click that picks TICKETS closes it and takes the reader to
  // them, on the page. Either way it is the page's one filter that changes.
  const showRows = () => {
    setWorkOpen(false);
    // Once the dialog has gone: it holds the page's scroll while it is open.
    requestAnimationFrame(() => document.getElementById(ROWS_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const pickPartner = (partner: string | null | undefined) =>
    set({ partner: partner !== undefined && filter.partner === partner ? undefined : partner });
  const pickCell = (partner: string | null, monthKey: string) => {
    const chosen =
      filter.partner === partner && filter.period?.event === "done" && filter.period.unit === "month" && filter.period.key === monthKey;
    // The same gesture in both directions: the chosen cell, clicked again, clears its period.
    if (chosen) return set({ period: undefined });
    setFilter((prev) => workCellFilter(prev, partner, monthKey));
    showRows();
  };
  const pickUntouched = (partner: string | null) => {
    if (filter.partner === partner && filter.statusContractor === null) return set({ statusContractor: undefined });
    setFilter((prev) => untouchedFilter(prev, partner));
    showRows();
  };

  // ── The drill-down. Clicking a bar narrows; clicking the same bar again goes back up. ──
  const { partner: noPartner, area: noCommune, priority: noPriority, typeVlsFo: noType } = empty;
  // The charts keep the dropdowns' rule: each is counted without its own filter.
  const byPartner = countByWithPriority(facetBase(rows, filter, "partner"), "buildContractor", noPartner);
  const byArea = countByWithPriority(facetBase(rows, filter, "area"), "area", noCommune);
  const byPriority = available.priority;

  const level = filter.partner === undefined ? "partner" : filter.area === undefined ? "area" : "priority";

  const crumbs: Array<{ label: string; onClick?: () => void }> = [
    {
      label: scopeLabels ? t("report.crumb.yourRows") : t("report.crumb.allPartners"),
      onClick: filter.partner === undefined ? undefined : () => set({ partner: undefined }),
    },
  ];
  if (filter.partner !== undefined) {
    crumbs.push({
      label: filter.partner ?? noPartner,
      onClick: filter.area === undefined ? undefined : () => set({ area: undefined }),
    });
  }
  if (filter.area !== undefined) {
    crumbs.push({
      label: filter.area ?? noCommune,
      onClick: filter.priority === undefined ? undefined : () => set({ priority: undefined }),
    });
  }
  if (filter.priority !== undefined) crumbs.push({ label: filter.priority ?? noPriority });

  return (
    <div className="px-4 py-6 lg:px-8 lg:py-8">
      {/* Freshness. Everybody reads the same figures all day, and the strip says which day. */}
      <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
        <CalendarClock className="h-4 w-4 text-slate-400" />
        <span>
          {t("report.freshness.snapshotOf", { date: formatDayMonthYear(dateOnly(snapshot.runDate), locale) })}
        </span>
        <span className="text-slate-300">·</span>
        <span>
          {t("report.freshness.counts", {
            stored: formatNumber(snapshot.itemCount),
            board: formatNumber(snapshot.boardItemCount),
          })}
        </span>
        {scopeLabels && (
          <>
            <span className="text-slate-300">·</span>
            {/* A contractor must never mistake their own slice for the whole board. */}
            <span className="pill pill-rose">
              {t("report.scope.showing", {
                labels: scopeLabels.length > 0 ? scopeLabels.join(", ") : t("report.scope.none"),
              })}
            </span>
          </>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <FacetSelect
          label={t("report.filter.partner")}
          groups={available.partner}
          value={filter.partner}
          onChange={(v) => set({ partner: v })}
        />
        <FacetSelect
          label={t("report.filter.commune")}
          groups={available.area}
          value={filter.area}
          onChange={(v) => set({ area: v })}
        />
        <FacetSelect label={t("report.filter.priority")} groups={available.priority} value={filter.priority} onChange={(v) => set({ priority: v })} />
        <FacetSelect
          label={t("report.filter.typeVlsFo")}
          groups={byType.groups}
          value={filter.typeVlsFo}
          onChange={(v) => set({ typeVlsFo: v })}
        />
        <FacetSelect
          label={t("report.filter.unifiber")}
          groups={available.statusUnifiber}
          value={filter.statusUnifiber}
          onChange={(v) => set({ statusUnifiber: v })}
        />
        <FacetSelect
          label={t("report.filter.contractor")}
          groups={available.statusContractor}
          value={filter.statusContractor}
          onChange={(v) => set({ statusContractor: v })}
        />

        <label className="flex items-center gap-2 text-xs text-slate-500">
          <input
            type="checkbox"
            className="tick"
            checked={filter.openOnly ?? true}
            onChange={(e) => set({ openOnly: e.target.checked })}
          />
          {t("report.filter.openOnly")}
        </label>

        {/* The period chosen on the in-and-out chart, echoed where the other filters are —
            it narrows everything on the page, so it has to be visible from the top of it. */}
        {filter.period && (
          <span className="chip-echo">
            {t("report.flow.chip", {
              event: t(`report.flow.short.${filter.period.event}`),
              period: periodText(filter.period, t, locale),
            })}
            <button
              type="button"
              onClick={() => set({ period: undefined })}
              title={t("report.flow.chip.clear")}
              aria-label={t("report.flow.chip.clear")}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {activeFilterCount(filter) > 0 && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setFilter({})}>
              <X className="mr-1 h-3.5 w-3.5" />
              {t("report.filter.clear", { n: activeFilterCount(filter) })}
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => downloadCsv(table.rows, filter, snapshot.runDate, scopeLabels, t)}
            title={t("report.export.hint")}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {t("report.export.button")}
          </button>
        </div>
      </div>

      {/* ── Headline figures, recomputed from whatever is filtered ── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          eyebrow={t("report.tile.openPunches")}
          value={formatNumber(f.openPunches)}
          icon={<TriangleAlert className="h-4 w-4" />}
          note={
            f.punchesWithoutPriority > 0
              ? t(f.punchesWithoutPriority === 1 ? "report.tile.openPunches.note.one" : "report.tile.openPunches.note", {
                  n: formatNumber(f.punchesWithoutPriority),
                })
              : undefined
          }
        >
          {(["P1", "P2", "P3"] as const).map((p) => (
            <span key={p} className={`pill ${p === "P1" ? "pill-red" : p === "P2" ? "pill-amber" : "pill-blue"}`}>
              {p} {formatNumber(f.byPriority[p])}
            </span>
          ))}
        </Tile>

        <Tile
          eyebrow={t("report.tile.dispatched")}
          value={formatNumber(f.dispatched)}
          icon={<Send className="h-4 w-4" />}
          note={t("report.tile.dispatched.note", { total: formatNumber(f.openPunches) })}
        />

        <Tile
          eyebrow={t("report.tile.done")}
          value={formatNumber(f.doneAwaitingValidation)}
          icon={<CheckCheck className="h-4 w-4" />}
          // ⚠ The punches Réginald sent back are not his queue, and the tile says where they
          // went rather than letting them vanish — 25 of 34 on 28 Sep 2026.
          note={
            f.sentBack > 0
              ? `${t("report.tile.done.note")} ${t(f.sentBack === 1 ? "report.tile.done.sentBack.one" : "report.tile.done.sentBack", {
                  n: formatNumber(f.sentBack),
                })}`
              : t("report.tile.done.note")
          }
        />

        <WorkTile onOpen={() => setWorkOpen(true)} />
      </div>

      {/* ── Behind the fourth tile: the contractors' work, then in and out (features 29, 27) ── */}
      {workOpen && work && inOut && (
        <Dialog title={t("report.work.title")} onClose={() => setWorkOpen(false)} size="report">
          <div className="space-y-5 p-6">
            <p className="text-xs leading-relaxed text-slate-500">{t("report.work.intro")}</p>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="overflow-x-auto">
                <WorkTableView
                  work={work}
                  filter={filter}
                  noPartner={noPartner}
                  onPartner={pickPartner}
                  onCell={pickCell}
                  onUntouched={pickUntouched}
                />
              </div>
            </div>
            <div className="space-y-1 text-xs leading-relaxed text-slate-400">
              {(() => {
                const week = (key: string) => t("report.flow.week", { n: weekNumberOf(key) });
                const range = (keys: string[]) => `${week(keys[0]!)}–${week(keys[keys.length - 1]!)}`;
                return (
                  <p>
                    {t("report.work.note.trend", {
                      recentRange: range(work.total.trend.recentWeeks),
                      beforeRange: range(work.total.trend.beforeWeeks),
                    })}
                  </p>
                );
              })()}
              <p>{t("report.work.note.columns")}</p>
              <p>{t("report.work.note.declared")}</p>
              {work.months
                .filter((m) => m.partial)
                .map((m) => (
                  <p key={m.key}>
                    {t("report.work.note.partial", {
                      month: `${formatMonthShort(m.key, locale)} ${m.key.slice(0, 4)}`,
                      date: formatDayMonthYear(dateOnly(snapshot.runDate), locale),
                    })}
                  </p>
                ))}
            </div>

            <Panel
              title={
                filter.partner === undefined
                  ? t("report.flow.titleAll")
                  : t("report.flow.titleFor", { partner: filter.partner ?? noPartner })
              }
              subtitle={t("report.flow.hint")}
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {(["week", "month"] as const).map((unit) => (
                  <button
                    key={unit}
                    type="button"
                    className={`chip ${shownUnit === unit ? "chip-active" : ""}`}
                    aria-pressed={shownUnit === unit}
                    onClick={() => {
                      // A period of the other unit has no bar on this chart: switching removes it,
                      // rather than leaving a filter on screen that no bar can click off.
                      if (filter.period !== undefined && filter.period.unit !== unit) set({ period: undefined });
                      setFlowUnit(unit);
                    }}
                  >
                    {t(`report.flow.unit.${unit}`)}
                  </button>
                ))}
              </div>
              <GroupedBars
                rows={inOut.periods.map((p) => ({
                  id: p.key,
                  label: periodLabel(p.key, shownUnit, t, locale),
                  muted: p.partial,
                  created: p.created,
                  done: p.done,
                  validated: p.validated,
                }))}
                series={FLOW_EVENTS.map((event) => ({
                  key: event,
                  label: t(`report.flow.series.${event}`),
                  color: FLOW_COLOUR[event],
                }))}
                // Wide enough that every bar carries its own value: the group totals the mockups
                // fall back on would add "in" to "validated", which means nothing.
                width={1000}
                height={240}
                groupTotals={false}
                selected={
                  filter.period !== undefined && filter.period.unit === shownUnit
                    ? { rowId: filter.period.key, seriesKey: filter.period.event }
                    : null
                }
                onSelect={(key, event) => {
                  // Clicking the chosen bar again clears it — the same gesture in both directions —
                  // and the dialog stays. A new bar is a list of tickets: the dialog closes on it.
                  const same = filter.period !== undefined && filter.period.key === key && filter.period.event === event;
                  if (same) return set({ period: undefined });
                  set({ period: { event: event as FlowEvent, unit: shownUnit, key } });
                  showRows();
                }}
                titleOf={(row, series, n) =>
                  `${periodText({ key: String(row.id), unit: shownUnit }, t, locale)} · ${series.label}: ${formatNumber(n)}`
                }
              />
              <div className="mt-3 space-y-1 text-xs leading-relaxed text-slate-400">
                <p>{t("report.flow.note.scope")}</p>
                <p>{t("report.flow.note.lastState")}</p>
                {inOut.periods
                  .filter((p) => p.partial)
                  .map((p) => (
                    <p key={p.key}>
                      {t("report.flow.note.partial", {
                        period: periodText({ key: p.key, unit: shownUnit }, t, locale),
                        date: formatDayMonthYear(dateOnly(snapshot.runDate), locale),
                      })}
                    </p>
                  ))}
                {(() => {
                  const undated = inOut.undated.created + inOut.undated.done + inOut.undated.validated;
                  return undated > 0 ? (
                    <p className="text-amber-700">
                      {t(undated === 1 ? "report.flow.note.undated.one" : "report.flow.note.undated", { n: formatNumber(undated) })}
                    </p>
                  ) : null;
                })()}
              </div>
            </Panel>
          </div>
        </Dialog>
      )}

      {/* ── Breadcrumb: the filter state, read out loud ── */}
      <nav className="mt-6 flex flex-wrap items-center gap-1 text-sm">
        {crumbs.map((crumb, i) => (
          <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
            {crumb.onClick ? (
              <button type="button" onClick={crumb.onClick} className="font-medium text-brand-rose hover:underline">
                {crumb.label}
              </button>
            ) : (
              <span className="font-semibold text-slate-700">{crumb.label}</span>
            )}
          </span>
        ))}
        <span className="ml-2 text-xs text-slate-400">
          {t(shown.length === 1 ? "report.rows.one" : "report.rows", { n: formatNumber(shown.length) })}
        </span>
      </nav>

      {/* ── The charts ── */}
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <Panel
          title={t(level === "partner" ? "report.panel.byPartner" : level === "area" ? "report.panel.byCommune" : "report.panel.byPriority")}
          subtitle={
            level === "priority" ? t("report.panel.deepest") : t("report.panel.drillHint")
          }
        >
          {/* Only where the bars are striped. At the deepest level each bar IS a priority,
              and a legend restating that would be noise. */}
          {level !== "priority" && (
            <div className="mb-3">
              <PriorityLegend />
            </div>
          )}
          {level === "partner" && (
            <HBars
              rows={byPartner.map((g) => ({ label: g.label, n: g.n, value: g.value, segments: segmentsOf(g.split, t) }))}
              selected={filter.partner === undefined ? null : (filter.partner ?? noPartner)}
              onSelect={(value) => set({ partner: value })}
              emptyNote={t("report.empty.noMatch")}
            />
          )}
          {level === "area" && (
            <HBars
              rows={byArea.map((g) => ({ label: g.label, n: g.n, value: g.value, segments: segmentsOf(g.split, t) }))}
              selected={filter.area === undefined ? null : (filter.area ?? noCommune)}
              onSelect={(value) => set({ area: value })}
              emptyNote={t("report.empty.noAreaRows")}
            />
          )}
          {level === "priority" && (
            <HBars
              rows={byPriority.map((g) => ({
                label: g.label,
                n: g.n,
                value: g.value,
                color: g.value ? PRIORITY_COLOUR[g.value] : CHART_PALETTE.slate,
              }))}
              selected={filter.priority === undefined ? null : (filter.priority ?? noPriority)}
              // Clicking the chosen bar again clears it, as HBars' own tooltip promises.
              onSelect={(value) => set({ priority: filter.priority === value ? undefined : value })}
              emptyNote={t("report.empty.noMatch")}
            />
          )}
        </Panel>

        <Panel
          title={t("report.panel.donutTitle")}
          subtitle={t("report.panel.donutHint")}
        >
          <Donut
            centreLabel={t("report.donut.centre")}
            slices={byPriority.map((g) => ({
              label: g.label,
              n: g.n,
              color: (g.value ? PRIORITY_COLOUR[g.value] : CHART_PALETTE.slate) ?? CHART_PALETTE.slate!,
            }))}
            selected={filter.priority === undefined ? null : (filter.priority ?? noPriority)}
            onSelect={(label) => {
              const group = byPriority.find((g) => g.label === label);
              // Clicking the active slice clears it — the same gesture in both directions.
              const already = filter.priority !== undefined && (filter.priority ?? noPriority) === label;
              set({ priority: already ? undefined : (group?.value ?? null) });
            }}
          />
        </Panel>
      </div>

      {/* ── Type VLS/FO, and age ── */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title={t("report.panel.byType")} subtitle={t("report.panel.byTypeHint")}>
          <HBars
            rows={byType.groups.map((g) => ({
              label: g.label,
              n: g.n,
              value: g.value,
              color: g.value === null ? CHART_PALETTE.slate : TYPE_COLOUR,
            }))}
            selected={filter.typeVlsFo === undefined ? null : (filter.typeVlsFo ?? noType)}
            // Clicking the chosen bar again clears it — the same gesture in both directions.
            onSelect={(value) => set({ typeVlsFo: filter.typeVlsFo === value ? undefined : value })}
            emptyNote={t("report.empty.noMatch")}
          />
          {/* ⚠ The one chart whose bars do not add up to its rows, and it says so. */}
          {byType.multiValued > 0 && (
            <p className="mt-3 text-xs leading-relaxed text-slate-400">
              {t(byType.multiValued === 1 ? "report.type.multi.one" : "report.type.multi", {
                multi: formatNumber(byType.multiValued),
                n: formatNumber(byType.rows),
                sum: formatNumber(byType.groups.reduce((s, g) => s + g.n, 0)),
              })}
            </p>
          )}
          {filter.typeVlsFo !== undefined && (
            <p className="mt-2 text-xs text-slate-400">{t("report.type.ownFilter")}</p>
          )}
        </Panel>

        <Panel title={t("report.panel.ageTitle")} subtitle={t("report.panel.ageHint")}>
          <HBars rows={ages.buckets.map((b) => ({ label: b.label, n: b.n, tone: b.tone }))} />
          {ages.unknown > 0 && (
            <p className="mt-3 text-xs text-slate-400">{t("report.age.unknown", { n: formatNumber(ages.unknown) })}</p>
          )}
        </Panel>
      </div>

      {/* ── Deadlines ── */}
      <div className="mt-4">
        <Panel title={t("report.panel.overdueTitle")} subtitle={t("report.panel.overdueHint")}>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-red-50 px-3 py-4">
              <p className="text-2xl font-bold tabular-nums text-red-700">{formatNumber(overdue.late)}</p>
              <p className="mt-1 text-[11px] font-medium text-red-600">{t("report.overdue.late")}</p>
            </div>
            <div className="rounded-lg bg-emerald-50 px-3 py-4">
              <p className="text-2xl font-bold tabular-nums text-emerald-700">{formatNumber(overdue.onTime)}</p>
              <p className="mt-1 text-[11px] font-medium text-emerald-600">{t("report.overdue.onTime")}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-4">
              <p className="text-2xl font-bold tabular-nums text-slate-500">{formatNumber(overdue.notApplicable)}</p>
              <p className="mt-1 text-[11px] font-medium text-slate-400">{t("report.overdue.na")}</p>
            </div>
          </div>
          {/* ⚠ Days over, not a red flag: with 7/15/30 days from dispatch, essentially every
              open punch breaches its deadline, and a screen that is entirely red informs
              nobody. The size of the breach is what distinguishes them. */}
          {overdue.worstDays !== null && (
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              {t("report.overdue.worst", {
                worst: formatNumber(overdue.worstDays),
                average: formatNumber(Math.round(overdue.totalLateDays / Math.max(1, overdue.late))),
              })}
            </p>
          )}
          <p className="mt-2 text-xs text-slate-400">{t("report.overdue.deadlines")}</p>
        </Panel>
      </div>

      {/* ── The leaf of the drill-down: the punches themselves ── */}
      <RowTable view={table} columns={columns} asOf={asOf} />

      {f.rowsWithoutPartner > 0 && (
        <p className="mt-4 text-xs text-slate-400">
          {t(f.rowsWithoutPartner === 1 ? "report.noPartner.note.one" : "report.noPartner.note", {
            n: formatNumber(f.rowsWithoutPartner),
          })}
        </p>
      )}
    </div>
  );
}

/**
 * The table's columns, and what each one sorts on.
 *
 * ⚠ Sorting only — no filter box under any heading. `SortableHead` can draw one, but a
 * column filter narrows the TABLE alone, while on this page the tiles, the charts and the
 * export all follow the one filter in the bar at the top. A second, table-only filter would
 * give the screen two answers to "what am I looking at".
 *
 * Every value is the board's own (a status sorts by its label) or a plain number, so the
 * shared comparator applies unchanged: text A→Z, numbers biggest first on the first click —
 * the oldest, the latest — and an empty value last in both directions.
 */
function rowColumns(t: (key: string) => string, asOf: Date | null): SortColumn<ReportRow>[] {
  return [
    { key: "address", label: t("report.table.address"), value: (r) => addressOf(r) },
    { key: "reference", label: t("report.table.reference"), value: (r) => r.name },
    {
      key: "typeVlsFo",
      label: t("report.table.typeVlsFo"),
      value: (r) => (r.typesVlsFo.length > 0 ? r.typesVlsFo.join(", ") : null),
    },
    { key: "priority", label: t("report.table.priority"), value: (r) => r.priority },
    { key: "partner", label: t("report.table.partner"), value: (r) => r.buildContractor },
    { key: "commune", label: t("report.table.commune"), value: (r) => r.area },
    { key: "unifiber", label: t("report.table.unifiber"), value: (r) => r.statusUnifiber },
    { key: "contractor", label: t("report.table.contractor"), value: (r) => r.statusContractor },
    // `YYYY-MM-DD` sorts chronologically as text; the first click puts the oldest dispatch on top.
    { key: "dispatched", label: t("report.table.dispatched"), value: (r) => r.dispatchDate },
    { key: "age", label: t("report.table.age"), value: (r) => (asOf === null ? null : ageOf(r, asOf)) },
    { key: "overdue", label: t("report.table.overdue"), value: (r) => (asOf === null ? null : daysOverdue(r, asOf)) },
  ];
}

/** The rows behind the figures, sortable by any heading. Capped, and honest about the cap. */
function RowTable({
  view,
  columns,
  asOf,
}: {
  view: TableView<ReportRow>;
  columns: SortColumn<ReportRow>[];
  asOf: Date;
}) {
  const { t, locale } = useI18n();
  const LIMIT = 100;
  const rows = view.rows;
  // ⚠ Sorted BEFORE the cap. Sorting only the hundred rows on screen would reorder a sample,
  // and "the hundred worst" is exactly what a click on the overdue heading asks for.
  const shown = rows.slice(0, LIMIT);

  return (
    <div id={ROWS_ANCHOR} className="mt-4 scroll-mt-20 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <p className="eyebrow">{t("report.table.title")}</p>
        <p className="text-xs text-slate-400">
          {rows.length > LIMIT
            ? t("report.table.capped", { limit: LIMIT, total: formatNumber(rows.length) })
            : t(rows.length === 1 ? "report.rows.one" : "report.rows", { n: formatNumber(rows.length) })}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="tbl">
          <SortableHead columns={columns} view={view} />
          <tbody>
            {shown.map((row) => (
              <tr key={row.mondayItemId}>
                {/* ⚠ The real address, from STREET_NM + HNR_FULL. The item's NAME is a
                    board reference — `CHAR007`, `SAMBRE009` — and it gets its own column
                    rather than masquerading as an address. */}
                <td className="max-w-[18rem] truncate font-medium text-slate-700" title={addressOf(row) ?? undefined}>
                  {addressOf(row) ?? <span className="text-slate-300">—</span>}
                </td>
                <td className="max-w-[10rem] truncate text-xs text-slate-400" title={row.name}>
                  {row.name}
                </td>
                {/* The board's text, verbatim and in its order — `FACADE, OTHER`. */}
                <td className="max-w-[12rem] truncate text-xs text-slate-600" title={row.typesVlsFo.join(", ") || undefined}>
                  {row.typesVlsFo.length > 0 ? row.typesVlsFo.join(", ") : <span className="text-slate-300">—</span>}
                </td>
                <td>
                  {row.priority ? (
                    <span
                      className={`pill ${row.priority === "P1" ? "pill-red" : row.priority === "P2" ? "pill-amber" : "pill-blue"}`}
                    >
                      {row.priority}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="text-slate-600">{row.buildContractor ?? <span className="text-slate-300">—</span>}</td>
                <td className="text-slate-600">{row.area ?? <span className="text-slate-300">—</span>}</td>
                <td className="text-slate-600">{row.statusUnifiber ?? <span className="text-slate-300">—</span>}</td>
                <td className="text-slate-600">{row.statusContractor ?? <span className="text-slate-300">—</span>}</td>
                <td className="num text-slate-500">
                  {row.dispatchDate ? formatDayMonthYear(dateOnly(row.dispatchDate), locale) : <span className="text-slate-300">—</span>}
                </td>
                <td className="num text-slate-500">
                  {(() => {
                    const age = ageOf(row, asOf);
                    return age === null ? <span className="text-slate-300">—</span> : t("report.days", { n: age });
                  })()}
                </td>
                <td className="num">
                  {(() => {
                    // ⚠ Days over, with its sign: a punch inside its deadline has days to
                    // spare, and null means the question does not apply — an unprioritised
                    // punch, or one with no dispatch date. Never "on time".
                    const over = daysOverdue(row, asOf);
                    if (over === null) return <span className="text-slate-300">—</span>;
                    return over > 0 ? (
                      <span className="pill pill-red">+{formatNumber(over)}</span>
                    ) : (
                      <span className="pill pill-emerald">{formatNumber(over)}</span>
                    );
                  })()}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="py-8 text-center text-slate-400">
                  {t("report.empty.noMatch")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Hand the browser the file.
 *
 * ⚠ Entirely local: no request, no server, nothing uploaded. The rows are already here and
 * already scoped, so the export is a pure function of what is on screen — which is exactly
 * what "it exports the filtered view" has to mean if the file is to match the figures
 * somebody just read.
 *
 * ⚠ And nothing here SENDS anything. The decision of 22 Sep is that a person mails these
 * from Outlook: a figure that is wrong must not be able to leave on its own.
 */
function downloadCsv(
  rows: ReportRow[],
  filter: QualityFilter,
  runDate: string,
  scopeLabels: string[] | null,
  t: (key: string) => string,
): void {
  const csv = toCsv(rows, { t, asOf: dateOnly(runDate) });
  // The name says which filter and which day, because these files get mailed on and an
  // `export.csv` tells the next reader nothing about the morning it describes.
  const name = exportFilename(
    [
      filter.partner ?? (scopeLabels?.length ? scopeLabels.join("-") : null),
      filter.area ?? null,
      filter.priority ?? null,
      filter.typeVlsFo ?? null,
      // "Validated in week 39" and "came in in week 39" are different files — the event is
      // in the name, not just the period.
      filter.period ? `${t(`report.flow.short.${filter.period.event}`)} ${filter.period.key}` : null,
    ],
    runDate,
  );

  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  // Without this the blob is held for the life of the document — a few hundred kilobytes
  // per export, on a screen people re-filter all afternoon.
  URL.revokeObjectURL(url);
}